import React, { useState, useRef, useEffect } from 'react';
import {
  BarChart3, Users, Search, Network, Swords,
  ChevronDown, Menu, X, Shield, LayoutGrid, Trophy, LineChart, Newspaper,
  ArrowRight, Loader2, History, Briefcase, Building2, ChevronRight
} from 'lucide-react';
import { SignedIn, SignedOut, SignInButton, SignUpButton } from '@clerk/clerk-react';
import ProfileDropdown from './ProfileDropdown';

// Navigation items configuration
const NAV_ITEMS = [
  { id: 'organizations', label: 'Organizations', icon: Building2 },
  { id: 'signals', label: 'Signals', icon: LineChart },
  { id: 'insights', label: 'Deals', icon: BarChart3 },
  { id: 'dashboard', label: 'Search', icon: Search },
  { id: 'ecosystem', label: 'Ecosystem', icon: Network },
  { id: 'matchup', label: 'Matchup', icon: Swords },
];

const ADMIN_NAV_ITEM = { id: 'admin', label: 'Admin', icon: Shield };

export default function TopNav({
  // Navigation
  activeTab,
  setActiveTab,
  // Sector
  sectorConfig,
  sectorPresets,
  onSectorChange,
  // Badges (counts)
  badges = {},
  // Theme
  theme = 'light',
  // Settings props (passed to ProfileDropdown)
  setTheme,
  userTier,
  user,
  // Admin
  isAdmin = false,
  // Logo click handler
  onLogoClick,
  // Search bar
  searchQuery = '',
  setSearchQuery,
  showSuggestions = false,
  setShowSuggestions,
  suggestions = [],
  recentSearches = [],
  searchLoading = false,
  onSearchSubmit,
  onSuggestionSelect,
  onSmartSearch,
  // Translations
  t = (key) => key,
}) {
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const profileRef = useRef(null);
  const searchRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfileDropdown(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions?.(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [setShowSuggestions]);

  const isDark = theme === 'dark';

  return (
    <nav className={`
      sticky top-0 z-50 
      ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'}
      border-b shadow-sm
    `}>
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center h-14 gap-3">
          
          {/* Logo + Name */}
          <div
            className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0"
            onClick={() => onLogoClick ? onLogoClick() : setActiveTab('insights')}
          >
            <img src="/logo192.png" alt="Global Infrastructure Sherpa" className="w-8 h-8 rounded-lg" />
            <span className={`hidden sm:inline text-sm font-semibold tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Global Infrastructure Sherpa
            </span>
          </div>

          {/* Compact Search Bar — Desktop (xl+), hidden on Search tab */}
          {activeTab === 'dashboard' ? (
            <div className="flex-1 min-w-0" />
          ) : (
            <>
              {/* Desktop inline search */}
              <div className="hidden xl:block flex-1 min-w-0 max-w-md mx-3" ref={searchRef}>
                <form onSubmit={onSearchSubmit} className="relative">
                  <div className={`relative flex items-center h-9 rounded-full border-2 transition-all shadow-sm
                    ${isDark
                      ? 'bg-slate-800 border-slate-500 focus-within:border-blue-500'
                      : 'bg-slate-50 border-slate-400 focus-within:border-blue-500'}
                    focus-within:shadow-md`}
                  >
                    <Search size={15} className={`ml-3 flex-shrink-0 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => { setSearchQuery(e.target.value); setShowSuggestions(true); }}
                      onFocus={() => setShowSuggestions(true)}
                      placeholder="Search infrastructure deals..."
                      className={`flex-1 h-full px-2 text-sm bg-transparent border-0 outline-none focus:outline-none focus:ring-0
                        ${isDark ? 'text-white placeholder-slate-500' : 'text-slate-900 placeholder-slate-400'}`}
                    />
                    <button
                      type="submit"
                      disabled={searchLoading || !searchQuery.trim()}
                      className={`flex-shrink-0 w-7 h-7 mr-1 rounded-full flex items-center justify-center transition-all
                        ${searchQuery.trim()
                          ? 'bg-blue-600 hover:bg-blue-700 text-white'
                          : isDark ? 'bg-slate-700 text-slate-500' : 'bg-slate-100 text-slate-400'}
                        disabled:opacity-50`}
                    >
                      {searchLoading ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <ArrowRight size={14} />
                      )}
                    </button>
                  </div>

                  {/* Typeahead dropdown */}
                  {showSuggestions && (() => {
                    const searchLower = searchQuery.toLowerCase();
                    const matchedRecent = searchQuery
                      ? recentSearches.filter(q => q.toLowerCase().includes(searchLower)).slice(0, 3)
                      : recentSearches.slice(0, 5);
                    const hasRecent = matchedRecent.length > 0;
                    const hasSuggestions = searchQuery && suggestions.length > 0;
                    if (!hasRecent && !hasSuggestions) return null;

                    return (
                      <div className={`absolute z-50 w-full mt-1 rounded-xl shadow-xl border overflow-hidden text-left
                        ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}
                      >
                        {hasRecent && (
                          <>
                            <div className={`px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                              Recent
                            </div>
                            {matchedRecent.map((q, i) => (
                              <button
                                key={`recent-${i}`}
                                onClick={() => { setSearchQuery(q); setShowSuggestions(false); onSmartSearch(q); }}
                                className={`w-full px-4 py-2 flex items-center gap-2.5 transition-colors text-sm
                                  ${isDark ? 'hover:bg-slate-700/70' : 'hover:bg-blue-50'}`}
                              >
                                <History size={13} className={isDark ? 'text-slate-400' : 'text-slate-400'} />
                                <span className={isDark ? 'text-slate-200' : 'text-slate-700'}>{q}</span>
                              </button>
                            ))}
                          </>
                        )}
                        {hasSuggestions && (
                          <>
                            {hasRecent && <div className={`border-t ${isDark ? 'border-slate-700' : 'border-slate-100'}`} />}
                            <div className={`px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                              Matches
                            </div>
                            {suggestions.map((item) => (
                              <button
                                key={`${item.kind}-${item.id}`}
                                onClick={() => onSuggestionSelect(item)}
                                className={`w-full px-4 py-2 flex items-center gap-2.5 transition-colors text-sm
                                  ${isDark ? 'hover:bg-slate-700/70' : 'hover:bg-blue-50'}`}
                              >
                                {item.kind === 'deal'
                                  ? <Briefcase size={13} className={isDark ? 'text-blue-400' : 'text-blue-500'} />
                                  : <Building2 size={13} className={isDark ? 'text-slate-400' : 'text-slate-500'} />}
                                <div className="flex-1 min-w-0">
                                  <p className={`font-medium text-sm truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>{item.name}</p>
                                  <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                                    {item.kind === 'deal'
                                      ? [item.type, item.country].filter(Boolean).join(' · ')
                                      : <>{item.category || item.type} {item.dealCount > 0 && `· ${item.dealCount} deal${item.dealCount !== 1 ? 's' : ''}`}</>}
                                  </p>
                                </div>
                                <ChevronRight size={12} className={isDark ? 'text-slate-600' : 'text-slate-300'} />
                              </button>
                            ))}
                          </>
                        )}
                        {searchQuery.trim() && (
                          <>
                            <div className={`border-t ${isDark ? 'border-slate-700' : 'border-slate-100'}`} />
                            <button
                              onClick={onSearchSubmit}
                              className={`w-full px-4 py-2 text-sm font-medium
                                ${isDark ? 'text-blue-400 hover:bg-slate-700/70' : 'text-blue-600 hover:bg-blue-50'}`}
                            >
                              Search all for "{searchQuery}" →
                            </button>
                          </>
                        )}
                      </div>
                    );
                  })()}
                </form>
              </div>

              {/* Mobile search icon — navigates to Search tab */}
              <button
                onClick={() => setActiveTab('dashboard')}
                className={`xl:hidden p-2 rounded-lg transition-colors ${isDark ? 'text-slate-400 hover:text-blue-400 hover:bg-slate-800' : 'text-slate-500 hover:text-blue-600 hover:bg-slate-100'}`}
                title="Search"
              >
                <Search size={18} />
              </button>

              {/* Desktop spacer to fill remaining space */}
              <div className="hidden xl:block" />
            </>
          )}

          {/* Navigation Items - Desktop */}
          <div className="hidden xl:flex items-center h-full">
            {[...NAV_ITEMS, ...(isAdmin ? [ADMIN_NAV_ITEM] : [])].map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              const badge = item.badgeKey ? badges[item.badgeKey] : null;

              return item.isExternal ? (
                <a
                  key={item.id}
                  href={item.href}
                  className={`
                    relative flex flex-col items-center justify-center px-3 h-full min-w-[72px]
                    border-b-2 border-transparent transition-colors
                    ${isDark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-700'}
                  `}
                >
                  <Icon size={20} />
                  <span className="text-xs mt-0.5">{item.label}</span>
                </a>
              ) : (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`
                    relative flex flex-col items-center justify-center px-3 h-full min-w-[72px]
                    border-b-2 transition-colors
                    ${isActive
                      ? `${isDark ? 'border-blue-800 text-blue-800' : 'border-blue-800 text-blue-800'}`
                      : `border-transparent ${isDark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-700'}`
                    }
                    ${item.id === 'admin' ? (isDark ? 'text-amber-400' : 'text-amber-600') : ''}
                  `}
                >
                  <Icon size={20} />
                  <span className="text-xs mt-0.5">{item.label}</span>
                  {badge > 0 && (
                    <span className={`
                      absolute top-1 right-1 min-w-[18px] h-[18px] px-1 text-[10px] font-semibold rounded-full
                      flex items-center justify-center
                      ${isDark ? 'bg-blue-700 text-white' : 'bg-blue-700 text-white'}
                    `}>
                      {badge > 999 ? '999+' : badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Right Section: Newsletter + Profile */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Newsletter */}
            <a
              href="https://globalinfrastructuresherpa.substack.com/"
              target="_blank"
              rel="noopener noreferrer"
              title="Newsletter"
              className={`p-2 rounded-lg transition-colors ${isDark ? 'text-slate-400 hover:text-orange-400 hover:bg-slate-800' : 'text-slate-500 hover:text-orange-600 hover:bg-slate-100'}`}
            >
              <Newspaper size={18} />
            </a>

            {/* Profile / Sign In */}
            <SignedOut>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setActiveTab('subscribe')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${isDark ? 'text-slate-300 hover:text-white hover:bg-slate-800' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'}`}
                >
                  Pricing
                </button>
                <SignInButton mode="modal">
                  <button className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${isDark ? 'text-slate-300 hover:text-white hover:bg-slate-800' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'}`}>
                    Sign in
                  </button>
                </SignInButton>
                <SignUpButton mode="modal">
                  <button className="px-4 py-1.5 bg-blue-700 hover:bg-blue-800 text-white rounded-lg text-sm font-medium transition-colors">
                    Register Free
                  </button>
                </SignUpButton>
              </div>
            </SignedOut>
            <SignedIn>
              <div ref={profileRef} className="relative">
                <button
                  onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                  className={`
                    flex flex-col items-center justify-center px-2 py-1 rounded-lg transition-colors
                    ${isDark ? 'hover:bg-slate-800' : 'hover:bg-slate-100'}
                  `}
                >
                  <div className="w-7 h-7 rounded-full bg-blue-700 text-white flex items-center justify-center text-xs font-semibold">
                    {user?.firstName?.charAt(0) || user?.emailAddresses?.[0]?.emailAddress?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <span className={`text-[10px] flex items-center gap-0.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    Me <ChevronDown size={10} />
                  </span>
                </button>

                {/* Profile Dropdown */}
                {showProfileDropdown && (
                  <ProfileDropdown
                    user={user}
                    theme={theme}
                    setTheme={setTheme}
                    userTier={userTier}
                    onNavigate={(tab) => {
                      setActiveTab(tab);
                      setShowProfileDropdown(false);
                    }}
                    onClose={() => setShowProfileDropdown(false)}
                    t={t}
                  />
                )}
              </div>
            </SignedIn>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className={`xl:hidden p-2 rounded-lg ${isDark ? 'hover:bg-slate-800' : 'hover:bg-slate-100'}`}
            >
              {showMobileMenu ? (
                <X size={20} className={isDark ? 'text-slate-300' : 'text-slate-600'} />
              ) : (
                <Menu size={20} className={isDark ? 'text-slate-300' : 'text-slate-600'} />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile/Tablet Navigation Menu */}
      {showMobileMenu && (
        <div className={`xl:hidden border-t ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'}`}>
          <div className="px-4 py-2 space-y-1">
            {[...NAV_ITEMS, ...(isAdmin ? [ADMIN_NAV_ITEM] : [])].map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              const badge = item.badgeKey ? badges[item.badgeKey] : null;

              return item.isExternal ? (
                <a
                  key={item.id}
                  href={item.href}
                  className={`
                    w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm
                    ${isDark ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-50'}
                  `}
                >
                  <Icon size={18} />
                  <span className="flex-1 text-left">{item.label}</span>
                </a>
              ) : (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveTab(item.id);
                    setShowMobileMenu(false);
                  }}
                  className={`
                    w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm
                    ${isActive
                      ? `${isDark ? 'bg-blue-700/10 text-blue-800' : 'bg-blue-50 text-blue-800'}`
                      : `${isDark ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-50'}`
                    }
                    ${item.id === 'admin' ? (isDark ? 'text-amber-400' : 'text-amber-600') : ''}
                  `}
                >
                  <Icon size={18} />
                  <span className="flex-1 text-left">{item.label}</span>
                  {badge > 0 && (
                    <span className={`px-2 py-0.5 text-xs rounded-full ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`}>
                      {badge}
                    </span>
                  )}
                </button>
              );
            })}

            {/* Mobile Sign In / Sign Up for logged out users */}
            <SignedOut>
              <div className={`pt-2 mt-2 border-t ${isDark ? 'border-slate-700' : 'border-slate-200'} flex gap-2`}>
                <SignInButton mode="modal">
                  <button className={`flex-1 px-3 py-2 text-sm font-medium rounded-xl transition-colors ${isDark ? 'text-slate-300 hover:bg-slate-800 border border-slate-700' : 'text-slate-600 hover:bg-slate-100 border border-slate-200'}`}>
                    Sign in
                  </button>
                </SignInButton>
                <button
                  onClick={() => { setActiveTab('subscribe'); setShowMobileMenu(false); }}
                  className="flex-1 px-3 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-xl text-sm font-medium transition-colors"
                >
                  Pricing
                </button>
              </div>
            </SignedOut>
          </div>
        </div>
      )}
    </nav>
  );
}